import { ColumnsWitdhType, Dataset, DatasetItem } from '../types/types';
import { formatValue } from './managerUtils';
import { getWindowCappedWidth } from './getWindowCappedWidth';

export const getColumnsWidth = (
  dataset: Dataset,
  data: any,
  oldColumnsWidth: ColumnsWitdhType,
  lazyState: any,
  recalculateHeader = false
): ColumnsWitdhType => {
  const formattedColumns = {};
  if (oldColumnsWidth && data.length === 0 && !recalculateHeader) {
    return oldColumnsWidth;
  }
  // First-wins to match the original find(); the width system keys by name, so
  // names are effectively unique, but this stays exact for duplicates too.
  const columnByName = new Map<string, DatasetItem>();
  (dataset.items ?? []).forEach((column) => {
    if (!columnByName.has(column.name)) columnByName.set(column.name, column);
  });
  dataset.items?.forEach((column) => {
    if (column.visible !== false) {
      // Add column label text with space for sorting and filtering buttons
      if (oldColumnsWidth && !recalculateHeader) {
        formattedColumns[column.name] = [];
      } else {
        let labelTemplate = `${column.label}........`;
        if (column.filterParam) {
          labelTemplate = `${labelTemplate}......`;
        }
        if (
          lazyState.multiSortMeta.length > 1 &&
          lazyState.multiSortMeta.some(({ field }) => field === column.name)
        ) {
          // Add space for multisort counter in header
          labelTemplate = `${labelTemplate}......`;
        }
        formattedColumns[column.name] = [labelTemplate];
      }
    }
  });
  // Create arrays for each column with formatted values
  data.forEach((row) => {
    Object.keys(row).forEach((key) => {
      const column = columnByName.get(key);
      if (column && formattedColumns[key]) {
        const formattedValue = formatValue(row, column).toString();
        formattedColumns[key].push(formattedValue);
      }
    });
  });
  const newColumnsWidth = {};
  const firstColumnName = dataset.items?.find(
    (column) => column.visible !== false
  )?.name;
  // Reuse one canvas/context; the font is constant, so measurements are identical.
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (context) {
    context.font = '700 13px "Poppins", sans-serif';
  }
  // Get longest text from each column
  Object.keys(formattedColumns).forEach((key) => {
    // Seed with '' — the column can be empty when its item was excluded from the request
    const longestText = formattedColumns[key].reduce(
      (a, b) => (a.length > b.length ? a : b),
      ''
    );
    if (context) {
      // Add paddings to text width
      let width = Math.ceil(context.measureText(longestText).width + 26);
      const column = columnByName.get(key);
      // Check if new width is older than old width of this column
      if (oldColumnsWidth && oldColumnsWidth[key] > width) {
        width = oldColumnsWidth[key];
      } else {
        // Check if calculated text width is not smaller than minWidth or larger than maxWidth
        if (column?.maxWidth) {
          const maxWidthPixels = column.maxWidth * 13;
          if (maxWidthPixels < width) {
            width = maxWidthPixels;
          }
        } else if (key !== firstColumnName) {
          width = getWindowCappedWidth(width, column?.minWidth);
        }
        if (column?.minWidth) {
          const minWidthPixels = column.minWidth * 13;
          if (minWidthPixels > width) {
            width = minWidthPixels;
          }
        }
      }
      newColumnsWidth[key] = width;
    }
  });
  return newColumnsWidth;
};
